/**
 * BookingHostCard — left-column host context for the public booking page.
 *
 * Renders the host identity (only for individual bookings — multi-host types
 * suppress the avatar and name because no single person represents the link),
 * the event title and description, plus a metadata row with duration, location
 * type, and timezone.
 *
 * Designed to slot into a three-column layout: host card | calendar | slots.
 */

import { Calendar, Clock, Globe01, VideoRecorder } from '@untitledui/icons';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';

import type {
  LocationType,
  PublicBookingType,
} from '@/lib/api/types/scheduling';
import { Avatar } from '@/lib/base/avatar/avatar';
import { shouldShowHostIdentity } from '@/lib/scheduling/hostIdentity';
import { cx } from '@/utils/cx';

import { TimezoneSelect } from './TimezoneSelect';

const DESCRIPTION_TRUNCATE_AT = 200;

function initialsFromDisplayName(name: string | undefined | null): string {
  if (!name) {
    return '';
  }
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] ?? ''}${parts[parts.length - 1][0] ?? ''}`.toUpperCase();
}

function locationLabel(locationType: LocationType): string {
  switch (locationType) {
    case 'google_meet':
      return 'Google Meet';
    case 'microsoft_teams':
      return 'Microsoft Teams';
    case 'zoom':
      return 'Zoom';
    case 'phone':
      return 'Phone call';
    case 'in_person':
      return 'In person';
    case 'custom':
      return 'Custom location';
    default:
      return locationType;
  }
}

interface MetadataRowProps {
  icon: FC<{ className?: string }>;
  children: React.ReactNode;
}

function MetadataRow({ icon: Icon, children }: MetadataRowProps) {
  return (
    <div className='flex items-center gap-2 text-sm text-[var(--color-text-secondary)]'>
      <Icon className='size-4 shrink-0 text-[var(--color-text-tertiary)]' />
      <span>{children}</span>
    </div>
  );
}

interface DescriptionProps {
  text: string;
}

function Description({ text }: DescriptionProps) {
  const [expanded, setExpanded] = useState(false);
  const isTruncatable = text.length > DESCRIPTION_TRUNCATE_AT;
  const handleToggle = useCallback(() => {
    setExpanded(prev => !prev);
  }, []);

  const displayText = useMemo(() => {
    if (!isTruncatable || expanded) {
      return text;
    }
    // Truncate at a word boundary near the cap to avoid cutting mid-word.
    const slice = text.slice(0, DESCRIPTION_TRUNCATE_AT);
    const lastSpace = slice.lastIndexOf(' ');
    return `${slice.slice(0, lastSpace > 100 ? lastSpace : DESCRIPTION_TRUNCATE_AT).trimEnd()}…`;
  }, [text, isTruncatable, expanded]);

  return (
    <p className='whitespace-pre-line text-sm text-[var(--color-text-secondary)] leading-relaxed'>
      {displayText}
      {isTruncatable && (
        <>
          {' '}
          <button
            type='button'
            onClick={handleToggle}
            className='font-medium text-fg-brand-primary hover:underline focus:outline-none focus:underline'
          >
            {expanded ? 'Show less' : 'Read more'}
          </button>
        </>
      )}
    </p>
  );
}

/**
 * Optional slot summary displayed at the top of the metadata row when the
 * booker has already picked a time (post-calendar step). Renders the date
 * and time range so the form-fill step still reminds the booker what
 * they're booking.
 */
export interface BookingHostCardSelectedSlot {
  /** Localised date, e.g. "Wednesday, May 13, 2026". */
  dateLabel: string;
  /** Localised time range, e.g. "5:00 PM – 5:30 PM". */
  timeLabel: string;
}

export interface BookingHostCardProps {
  meetingType: PublicBookingType;
  timezone: string;
  /** Called when the booker picks a different viewing timezone. */
  onTimezoneChange?: (next: string) => void;
  /** IANA timezones the booker can switch to. */
  availableTimezones?: readonly string[];
  /** When present, renders selected-slot rows at the top of the metadata. */
  selectedSlot?: BookingHostCardSelectedSlot;
  className?: string;
}

export function BookingHostCard({
  meetingType,
  timezone,
  onTimezoneChange,
  availableTimezones,
  selectedSlot,
  className,
}: BookingHostCardProps) {
  // Multi-host bookings have no single visible owner — collective bookings
  // include everyone, round_robin picks someone at booking-time. In both
  // cases showing one face / name on the link is misleading, so we suppress
  // the host-identity block entirely and lead with the event title.
  const showHostIdentity = shouldShowHostIdentity(meetingType);

  const displayName =
    meetingType.owner_display_name || meetingType.owner_username;
  const initials = initialsFromDisplayName(displayName);
  const eventTitle = meetingType.display_name || meetingType.name;
  const description = meetingType.description?.trim() ?? '';

  return (
    <aside className={cx('flex flex-col gap-3 md:gap-4', className)}>
      {showHostIdentity && (
        // Avatar+name lays out horizontally on mobile (saves vertical real
        // estate above the calendar) and stacks vertically on tablet+ where
        // the host card has its own column and breathing room.
        //
        // Avatar sizing note: we render `size='lg'` consistently rather than
        // overriding the container with `md:size-12`. The `size` prop drives
        // both the root size AND the inner text/icon classes — overriding
        // only the container leaves the fallback initials at `md`-sized
        // text, which looks visibly undersized in a `size-12` ring on
        // tablet/desktop. A 48px avatar beside the host name on mobile is
        // still well-proportioned alongside the 14px name.
        <div className='flex items-center gap-3 md:flex-col md:items-start md:gap-2'>
          <Avatar
            size='lg'
            src={meetingType.owner_avatar_url || undefined}
            initials={initials || undefined}
            alt={displayName}
          />
          <p className='text-sm font-medium text-[var(--color-text-secondary)]'>
            {displayName}
          </p>
        </div>
      )}

      <h1 className='text-xl font-semibold leading-tight text-[var(--color-text-primary)] md:text-2xl'>
        {eventTitle}
      </h1>

      {/* Description hidden on mobile — booker's primary task is picking a
          slot, and on phones the description pushes the calendar far below
          the fold. Visible on md+ where there's room. */}
      {description && (
        <div className='hidden md:block'>
          <Description text={description} />
        </div>
      )}

      <div className='mt-1 flex flex-col gap-2 border-t border-[var(--color-border-secondary)] pt-4'>
        {selectedSlot && (
          <>
            <MetadataRow icon={Calendar}>{selectedSlot.dateLabel}</MetadataRow>
            <MetadataRow icon={Clock}>{selectedSlot.timeLabel}</MetadataRow>
            <div className='my-1 border-t border-dashed border-[var(--color-border-secondary)]' />
          </>
        )}
        <MetadataRow icon={Clock}>
          {meetingType.duration_minutes} min
        </MetadataRow>
        <MetadataRow icon={VideoRecorder}>
          {locationLabel(meetingType.location_type)}
        </MetadataRow>
        <MetadataRow icon={Globe01}>
          {onTimezoneChange &&
          availableTimezones &&
          availableTimezones.length > 0 ? (
            <TimezoneSelect
              value={timezone}
              onChange={onTimezoneChange}
              options={availableTimezones}
            />
          ) : (
            timezone.replace(/_/g, ' ')
          )}
        </MetadataRow>
      </div>
    </aside>
  );
}
